import React, { useEffect, useMemo, useState } from 'react';

import ModuloSubmoduloForm from './ModuloSubmoduloForm';
import BoxConteudo from '../../../../../../../components/BoxConteudo';
import CustomButton from '../../../../../../../components/CustomButton';
import CustomCol from '../../../../../../../components/CustomCol';
import { type CustomTableHeader } from '../../../../../../../components/CustomTable';
import DetailsTable from '../../../../../../../components/CustomTable/DetailsTable';
import CustomTableOffcanvas from '../../../../../../../components/CustomTableOffcanvas';
import { getUser } from '../../../../../../../modules/contexts/Auth/RequireAuth';
import {
  IModuloSubmoduloForm,
  IModuloSubmoduloRegistro,
} from '../../../../../../../modules/types/ModuloSisaf';
import { getModuloSubmodulosByModuloId } from '../../../../../../../services/modulosSisaf/moduloSubmoduloService';
import theme from '../../../../../../../theme';
import { useExcelExport } from '../../../../../../../utils/excel-export';
import {
  formataNomeMeioSiglas,
  formatDate,
  formatDateToFile,
} from '../../../../../../../utils/functions';
import { handleDelete } from '../../../../../../../utils/handleDelete';
import { optionStatusBin } from '../../../../Dominios/utils/optionStatusBin';

type SubmoduloTableRow = {
  id: number;
  status: React.ReactNode;
  submodulo: string;
  descricao: string;
  itens: React.ReactNode;
};

const headersTable: CustomTableHeader<SubmoduloTableRow>[] = [
  { text: 'ID', key: 'id', width: 6 },
  { text: 'Status', key: 'status', width: 12 },
  { text: 'Submódulo', key: 'submodulo', width: 22 },
  { text: 'Descrição', key: 'descricao', width: 30 },
  { text: 'Itens', key: 'itens', width: 30 },
];

const valoresOffcanvas: Record<string, string> = {
  id: 'ID',
  dataRegistro: 'Registro',
  dataUltimaAtualizacao: 'Última Atualização',
  dataUltimaVisualizacao: 'Última Visualização',
  nEdicoes: 'Edições',
  nVisualizacoes: 'Visualizações',
  nVisualizadores: 'Visualizadores',

  separadorLog: '',
  separadorDados: '',

  status: 'Status',
  moduloNome: 'Módulo',
  submodulo: 'Submódulo',
  descricao: 'Descrição',
  itens: 'Itens',
};

const statusBadge = (status: number) => (
  <span
    style={{
      backgroundColor:
        status === 1 ? theme.colors.verdegov : theme.colors.vermelho,
      borderRadius: '10px',
      padding: '8px 10px',
      display: 'inline-block',
      minWidth: '70px',
      textAlign: 'center',
      color: 'white',
      fontWeight: 600,
    }}
  >
    {optionStatusBin.find((opt) => opt.value === status)?.label || 'N/I'}
  </span>
);

interface ModuloSubmodulosProps {
  moduloId: number;
  onLogsRefresh?: () => void;
}

const ModuloSubmodulos: React.FC<ModuloSubmodulosProps> = ({
  moduloId,
  onLogsRefresh,
}) => {
  const user = getUser();
  const userID = user.sub;
  const [submodulos, setSubmodulos] = useState<IModuloSubmoduloRegistro[]>([]);
  const [selectedOffcanvasId, setSelectedOffcanvasId] = useState<number | null>(
    null,
  );
  const [selectedSubmodulo, setSelectedSubmodulo] =
    useState<IModuloSubmoduloRegistro | null>(null);
  const [showForm, setShowForm] = useState(false);

  const normalizeText = (value?: string | null) =>
    (value ?? '').trim().toLowerCase();

  const findCreatedSubmodulo = (
    registros: IModuloSubmoduloRegistro[],
    preferred?: IModuloSubmoduloForm | null,
  ) => {
    if (!preferred) return null;

    if (preferred.id) {
      return registros.find((item) => item.id === preferred.id) || null;
    }

    const submodulo = normalizeText(preferred.submodulo);
    const descricao = normalizeText(preferred.descricao);
    const status = Number(preferred.status);

    const matches = registros.filter(
      (item) =>
        normalizeText(item.submodulo) === submodulo &&
        normalizeText(item.descricao) === descricao &&
        Number(item.status) === status,
    );

    if (matches.length === 0) return null;

    return matches.sort((a, b) => b.id - a.id)[0];
  };

  const fetchSubmodulos = async (preferred?: IModuloSubmoduloForm | null) => {
    try {
      const registros = await getModuloSubmodulosByModuloId(moduloId);
      setSubmodulos(registros);

      const justCreated = findCreatedSubmodulo(registros, preferred);
      if (justCreated) {
        setSelectedSubmodulo(justCreated);
        return;
      }

      if (selectedSubmodulo) {
        const atualizado =
          registros.find((item) => item.id === selectedSubmodulo.id) || null;
        setSelectedSubmodulo(atualizado);
        return;
      }

      setSelectedSubmodulo(registros.length > 0 ? registros[0] : null);
    } catch (error) {
      console.error('Erro ao buscar submódulos:', error);
      setSubmodulos([]);
    }
  };

  useEffect(() => {
    fetchSubmodulos();
  }, [moduloId]);

  useEffect(() => {
    setSelectedSubmodulo(null);
    setSelectedOffcanvasId(null);
  }, [moduloId]);

  const tableData: SubmoduloTableRow[] = useMemo(
    () =>
      submodulos.map((item) => ({
        id: item.id,
        status: statusBadge(item.status),
        submodulo: item.submodulo,
        descricao: item.descricao || '—',
        itens: item.itens || '-',
      })),
    [submodulos],
  );

  const offCanvasData = useMemo(
    () =>
      submodulos.map((item) => ({
        id: item.id,
        dataRegistro:
          formatDate(item.dataRegistro) +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioRegistro.nomeCompleto),
        dataUltimaAtualizacao:
          formatDate(item.dataUltimaAtualizacao) +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioAtualizacao.nomeCompleto),
        dataUltimaVisualizacao:
          formatDate(item.dataUltimaVisualizacao) +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioUltimaVisualizacao.nomeCompleto),
        nEdicoes: item.nEdicoes,
        nVisualizacoes: item.nVisualizacoes,
        nVisualizadores: item.nVisualizadores,

        separadorLog: '',
        status: statusBadge(item.status),

        separadorDados: '',
        moduloNome: item.moduloNome || '-',
        submodulo: item.submodulo,
        descricao: item.descricao || 'Não informado',
        itens: item.itens || '-',
      })),
    [submodulos],
  );

  const handleSelectSubmodulo = (row: SubmoduloTableRow) => {
    const registro =
      submodulos.find((sub) => String(sub.id) === String(row.id)) || null;
    setSelectedSubmodulo(registro);
    setShowForm(true);
  };

  const handleOpenDetails = (row: SubmoduloTableRow) => {
    if (!row?.id) return;
    setSelectedOffcanvasId(row.id);
  };

  const handleNew = () => {
    setSelectedSubmodulo(null);
    setShowForm(true);
  };

  const handleDeleteWrapper = async (id: number) => {
    const deleted = await handleDelete(id, 'modulossubmodulos');
    if (!deleted) return;

    if (selectedSubmodulo?.id === id) {
      setSelectedSubmodulo(null);
    }
    fetchSubmodulos();
    onLogsRefresh?.();
  };

  const { exportData, isExporting } = useExcelExport(
    'listagemmodulossubmodulos',
  );

  const handleExport = async () => {
    try {
      await exportData(
        submodulos,
        `sisaf_modulos_submodulos_${formatDateToFile(
          new Date().toISOString(),
        )}.xlsx`,
        userID,
        'Listagem de Submódulos',
        'DAF',
      );
    } catch (error) {
      console.error('Erro na exportação de submódulos:', error);
    }
  };

  return (
    <>
      <BoxConteudo boxTitulo="Submódulos">
        <CustomTableOffcanvas
          headers={headersTable}
          data={tableData}
          displayValues={valoresOffcanvas}
          offcanvasTitle="Submódulo"
          widthOffcanvas="45%"
          onOpenOffcanvas={handleOpenDetails}
          onRowClick={handleSelectSubmodulo}
          showOpenButton
          showPagination={false}
          filterable={false}
          minHeight="400px"
          data-testid="modulos-submodulos-table"
        >
          {(row) => (
            <DetailsTable
              rows={offCanvasData}
              selectedId={row?.id ?? selectedOffcanvasId}
              displayValues={valoresOffcanvas}
            />
          )}
        </CustomTableOffcanvas>

        <CustomCol className="d-flex justify-content-end gap-3 mt-3">
          <CustomButton
            label="Exportar Dados"
            bgColor={theme.colors.botaoAcao.amarelo}
            textColor={theme.colors.black}
            onClick={handleExport}
            deactivate={isExporting}
          />
          <CustomButton
            label="Novo Submódulo"
            bgColor={theme.colors.botaoAcao.azul}
            textColor={theme.colors.white}
            onClick={handleNew}
          />
        </CustomCol>
      </BoxConteudo>

      {showForm && (
        <ModuloSubmoduloForm
          moduloId={moduloId}
          registro={selectedSubmodulo}
          onClose={() => setShowForm(false)}
          onRefresh={fetchSubmodulos}
          onSaved={(savedForm) => {
            fetchSubmodulos(savedForm);
            onLogsRefresh?.();
          }}
          onDeleted={(id) => {
            setShowForm(false);
            handleDeleteWrapper(id);
          }}
          onLogsRefresh={onLogsRefresh}
        />
      )}
    </>
  );
};

export default ModuloSubmodulos;
